import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowRightLong, RightChevron } from "@/components/icons";
import { Book } from "@/components/icons";
import type { OngoingTraining } from "../../types/client-dashboard.types";
import styles from "./OngoingTrainingsList.module.scss";

function getProgressColor(percent: number): "orange" | "blue" {
  return percent >= 80 ? "blue" : "orange";
}

export interface OngoingTrainingsListProps {
  items: OngoingTraining[];
}

export const OngoingTrainingsList: React.FC<OngoingTrainingsListProps> = ({
  items,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formatProgress = (item: OngoingTraining) => {
    if (item.progressType === "units" && item.unitLabel) {
      return `${item.current}/${item.total} ${item.unitLabel}`;
    }
    return `${item.current}/${item.total}`;
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>
          {t("clientDashboard.ongoingTrainings.title")}
        </h3>
        <button
          type="button"
          className={styles.sectionLink}
          onClick={() => navigate("/dashboard/client-educations")}
          aria-label={t("clientDashboard.ongoingTrainings.viewAll")}
        >
          <RightChevron width={20} height={20} />
        </button>
      </div>
      <ul className={styles.list}>
        {items.map((item) => {
          const barColor = getProgressColor(item.progressPercent);
          return (
            <li key={item.id} className={styles.item}>
              <div className={styles.itemTopRow}>
                <div className={styles.iconWrap}>
                  <Book width={24} height={24} color="#E7212B" />
                </div>
                <div className={styles.itemMain}>
                  <span className={styles.itemTitle}>{item.title}</span>
                  <span className={styles.itemInstructor}>
                    {item.instructor}
                  </span>
                  <span className={styles.itemMeta}>
                    {formatProgress(item)}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.detailBtn}
                  onClick={() => navigate("/dashboard/client-educations")}
                  aria-label={t("clientDashboard.ongoingTrainings.detail")}
                >
                  <ArrowRightLong width={18} height={18} />
                </button>
              </div>
              <div className={styles.progressRow}>
                <div className={styles.progressTrack}>
                  <div
                    className={`${styles.progressFill} ${styles[`progressFill_${barColor}`]}`}
                    style={{ width: `${item.progressPercent}%` }}
                  />
                </div>
                <span
                  className={`${styles.percentLabel} ${styles[`percentLabel_${barColor}`]}`}
                >
                  %{item.progressPercent}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OngoingTrainingsList;
